<template>
  <v-dialog :model-value="open" max-width="760" scrollable aria-labelledby="mark-shipped-title" @update:model-value="(value: boolean) => !value && emit('close')">
    <div ref="root" class="flex max-h-[90vh] flex-col rounded-xl bg-white shadow-xl">
      <header class="border-b border-gray-200 px-5 py-4">
        <h2 id="mark-shipped-title" class="m-0 text-lg font-semibold text-gray-900">Mark shipped</h2>
        <p class="m-0 mt-1 text-sm text-gray-500">Add the POs that left together and tick the planned shipments. The HAWB/BOL can be added later.</p>
      </header>

      <div class="flex flex-col gap-4 overflow-y-auto px-5 py-4">
        <div class="grid gap-3 sm:grid-cols-3">
          <div class="flex flex-col gap-1">
            <label class="label flex flex-col gap-1">
              Shipped on
              <input
                v-model="shippedOn"
                type="date"
                :max="latest"
                class="input h-9"
                :class="bad('shippedOn')"
                data-field="shippedOn"
                :aria-invalid="error?.field === 'shippedOn'"
                :aria-describedby="describedBy('shippedOn')"
              />
            </label>
            <span v-if="error?.field === 'shippedOn'" :id="errorId('shippedOn')" class="text-xs text-red-700">{{ error.message }}</span>
          </div>
          <div class="flex flex-col gap-1">
            <label class="label flex flex-col gap-1">
              Forwarder
              <select
                v-model="forwarder"
                class="select h-9"
                :class="bad('forwarder')"
                data-field="forwarder"
                :aria-invalid="error?.field === 'forwarder'"
                :aria-describedby="describedBy('forwarder')"
              >
                <option value="">Choose a forwarder</option>
                <option v-for="f in FORWARDERS" :key="f" :value="f">{{ f }}</option>
              </select>
            </label>
            <span v-if="error?.field === 'forwarder'" :id="errorId('forwarder')" class="text-xs text-red-700">{{ error.message }}</span>
          </div>
          <label class="label flex flex-col gap-1">
            <span>
              HAWB/BOL
              <span class="font-normal text-gray-500">(optional)</span>
            </span>
            <input v-model="hawb" class="input h-9 font-mono" :maxlength="HAWB_MAX" placeholder="e.g. LKY26101701" data-field="hawb" />
          </label>
        </div>

        <div>
          <span class="label">
            Packing list
            <span class="font-normal text-gray-500">(optional)</span>
          </span>
          <PackingListInput
            id="mark-shipped-packing-list"
            v-model="file"
            class="mt-1"
            hint=".xlsx or .csv, up to 10 MB. Without it, the shipment shows Packing list missing until you add it."
            :error="error?.field === 'file' ? error.message : null"
          />
        </div>

        <section v-for="card in cards" :key="card.poNumber" class="overflow-hidden rounded-lg border border-gray-200" :aria-label="`Planned shipments of ${card.poNumber}`">
          <header class="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-gray-100 px-3 py-2">
            <b class="text-sm text-gray-900">{{ card.poNumber }}</b>
            <span class="text-xs text-gray-500">{{ tickedCount(card) }} of {{ card.shipments.length }} planned shipments ticked</span>
            <button type="button" :class="REMOVE_BUTTON" :aria-label="`Take ${card.poNumber} off this shipment`" @click="removeCard(card.poNumber)">×</button>
          </header>
          <ul class="m-0 list-none p-0">
            <li v-for="s in card.shipments" :key="s.id" class="border-t border-gray-100 first:border-t-0">
              <!-- Below sm the date, pieces and note stack in the second column. -->
              <label
                class="grid cursor-pointer grid-cols-[20px_minmax(0,1fr)] items-start gap-x-3 gap-y-0.5 px-3 py-2 text-sm hover:bg-gray-50 sm:grid-cols-[20px_130px_140px_minmax(0,1fr)] sm:items-center sm:gap-y-0"
              >
                <input type="checkbox" class="mt-1 sm:mt-0" :checked="ticked.has(s.id)" @change="toggle(s.id)" />
                <span class="flex flex-wrap items-center gap-2" :class="s.plannedDate < today ? 'font-semibold text-red-700' : 'text-gray-900'">
                  {{ fmtDay(s.plannedDate) }}
                  <span v-if="s.plannedDate < today" class="badge font-semibold" :class="SHIPMENT_STATE_TONE.late">Late</span>
                </span>
                <span class="col-start-2 tabular-nums text-gray-700 sm:col-start-auto">{{ fmtPcs(pieces(s)) }} pcs · {{ linesLabel(s.lines.length) }}</span>
                <span v-if="s.note" class="col-start-2 break-words text-xs text-gray-500 sm:col-start-auto">{{ s.note }}</span>
              </label>
            </li>
          </ul>
        </section>
        <p v-if="error?.field === 'shipmentIds'" :id="errorId('shipmentIds')" class="m-0 text-sm text-red-700" role="alert">{{ error.message }}</p>

        <div class="flex flex-col gap-1">
          <label class="label" for="mark-shipped-add-po">{{ cards.length ? 'Add another PO' : 'Which PO is shipping?' }}</label>
          <div class="flex flex-wrap gap-2">
            <input
              id="mark-shipped-add-po"
              v-model="poQuery"
              class="input h-9 w-full sm:!w-56"
              placeholder="PO number, e.g. P11504"
              autocomplete="off"
              data-field="addPo"
              :aria-invalid="addError !== null"
              :aria-describedby="addError ? addErrorId : describedBy('shipmentIds')"
              @keydown.enter.prevent="addPo"
            />
            <button type="button" class="btn-secondary !py-1.5" :disabled="adding" @click="addPo">{{ adding ? 'Adding…' : 'Add' }}</button>
          </div>
          <!-- An alert: Add can be clicked, so focus isn't always on the field this belongs to. -->
          <span v-if="addError" :id="addErrorId" class="text-xs text-red-700" role="alert">{{ addError }}</span>
        </div>

        <p v-if="serverError" class="m-0 text-sm text-red-700" role="alert">{{ serverError }}</p>
      </div>

      <footer class="flex flex-wrap items-center justify-end gap-3 border-t border-gray-200 px-5 py-3">
        <span class="mr-auto text-sm tabular-nums text-gray-500">{{ footerText }}</span>
        <button type="button" class="btn-secondary" @click="emit('close')">Cancel</button>
        <button type="button" class="btn-primary" :disabled="saving" @click="submit">{{ saving ? 'Shipping…' : 'Mark shipped' }}</button>
      </footer>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
  import { computed, nextTick, ref } from 'vue';

  import PackingListInput from '@/components/poTracker/PackingListInput.vue';
  import { REMOVE_BUTTON, SHIPMENT_STATE_TONE } from '@/components/poTracker/poTrackerStyles';
  import { useFieldError } from '@/composables/useFieldError';
  import logger from '@/services/logger';
  import { fetchPoDetail, markShipped, uploadPackingList } from '@/services/poTrackerService';
  import { usePoTrackerStore } from '@/store/poTrackerStore';
  import { SHIPMENT_STATUS, type PoDetail, type Shipment } from '@/types/poTracker';
  import { extractErrorMessage } from '@/utils/apiError';
  import { localIsoToday } from '@/utils/dates';
  import {
    FORWARDERS,
    fmtDay,
    fmtPcs,
    isoToday,
    latestShippedOn,
    linesLabel,
    noPlannedShipmentsText,
    packingListErrorMessage,
    packingListFileError,
    shipPoError,
    sortShipments,
    tickedShipments,
    validateMarkShipped,
    type FieldError,
  } from '@/utils/poTracker';

  const HAWB_MAX = 60;

  interface PoCard {
    poNumber: string;
    shipments: Shipment[];
  }

  const props = defineProps<{ open: boolean; vendorId: string; initial: { detail: PoDetail; shipmentId: string } | null }>();
  const emit = defineEmits<{ close: [] }>();

  const store = usePoTrackerStore();
  const root = ref<HTMLElement | null>(null);
  const today = isoToday();
  const latest = latestShippedOn(today);

  // The vendor's own calendar day; the check against latest still allows a day ahead of UTC.
  const shippedOn = ref(localIsoToday());
  const forwarder = ref('');
  const hawb = ref('');
  const file = ref<File | null>(null);
  const cards = ref<PoCard[]>(props.initial ? [toCard(props.initial.detail)] : []);
  const ticked = ref<Set<string>>(new Set(props.initial ? [props.initial.shipmentId] : []));
  const poQuery = ref('');
  const addError = ref<string | null>(null);
  const error = ref<FieldError | null>(null);
  const serverError = ref<string | null>(null);
  const saving = ref(false);
  const adding = ref(false);

  const pickedShipments = computed(() => tickedShipments(cards.value, ticked.value));
  const footerText = computed(() => {
    const picked = pickedShipments.value;
    if (picked.length === 0) return 'Nothing ticked yet';
    const pcs = picked.reduce((sum, s) => sum + pieces(s), 0);
    return `${picked.length} ${picked.length === 1 ? 'shipment' : 'shipments'} · ${fmtPcs(pcs)} pcs`;
  });

  const { errorId, describedBy, bad } = useFieldError(error);
  const addErrorId = errorId('addPo');

  function toCard(detail: PoDetail): PoCard {
    return { poNumber: detail.poNumber, shipments: sortShipments(detail.shipments.filter((s) => s.status === SHIPMENT_STATUS.PLANNED)) };
  }

  function pieces(s: Shipment): number {
    return s.lines.reduce((sum, l) => sum + l.quantity, 0);
  }

  function tickedCount(card: PoCard): number {
    return card.shipments.filter((s) => ticked.value.has(s.id)).length;
  }

  function toggle(id: string): void {
    const next = new Set(ticked.value);
    if (!next.delete(id)) next.add(id);
    ticked.value = next;
  }

  function removeCard(poNumber: string): void {
    const card = cards.value.find((c) => c.poNumber === poNumber);
    const next = new Set(ticked.value);
    card?.shipments.forEach((s) => next.delete(s.id));
    ticked.value = next;
    cards.value = cards.value.filter((c) => c.poNumber !== poNumber);
  }

  async function addPo(): Promise<void> {
    if (adding.value) return;
    const po = poQuery.value.trim().toUpperCase();
    addError.value = shipPoError(po, cardPos());
    if (addError.value) return;
    adding.value = true;
    let detail: PoDetail;
    try {
      detail = await fetchPoDetail(props.vendorId, po, true);
    } catch (fetchError) {
      addError.value = extractErrorMessage(fetchError);
      return;
    } finally {
      adding.value = false;
    }
    // Checked again: a card can have been added while the fetch ran.
    addError.value = shipPoError(detail.poNumber, cardPos());
    if (addError.value) return;
    const card = toCard(detail);
    if (card.shipments.length === 0) {
      addError.value = noPlannedShipmentsText(po);
      return;
    }
    cards.value = [...cards.value, card];
    poQuery.value = '';
  }

  function cardPos(): string[] {
    return cards.value.map((c) => c.poNumber);
  }

  function focusField(field: string): void {
    void nextTick(() => root.value?.querySelector<HTMLElement>(`[data-field="${field}"]`)?.focus());
  }

  function validate(): FieldError | null {
    const shipmentIds = pickedShipments.value.map((s) => s.id);
    const base = validateMarkShipped({ shippedOn: shippedOn.value, forwarder: forwarder.value, shipmentIds, today });
    if (base) return base;
    const fileError = file.value ? packingListFileError(file.value) : null;
    return fileError ? { field: 'file', message: fileError } : null;
  }

  async function submit(): Promise<void> {
    if (saving.value) return;
    serverError.value = null;
    error.value = validate();
    if (error.value) {
      focusField(error.value.field === 'shipmentIds' ? 'addPo' : error.value.field);
      return;
    }
    const picked = pickedShipments.value;
    const waybill = hawb.value.trim();
    saving.value = true;
    try {
      const event = await markShipped({
        vendorId: props.vendorId,
        shippedOn: shippedOn.value,
        forwarder: forwarder.value,
        ...(waybill ? { hawb: waybill } : {}),
        shipmentIds: picked.map((s) => s.id),
      });
      const shipped = `${picked.length} ${picked.length === 1 ? 'shipment' : 'shipments'} shipped`;
      if (file.value) {
        try {
          await uploadPackingList(event.id, file.value, null);
          logger.showSuccess(shipped);
        } catch (uploadError) {
          // The shipments stay shipped; the packing list can be added from the shipment.
          logger.showWarning(`Shipped. The packing list didn't upload: ${packingListErrorMessage(uploadError).replace(/\.$/, '')}. Add it from the shipment.`);
        }
      } else {
        logger.showSuccess(shipped);
      }
      emit('close');
      await store.afterWrite(event.poNumbers);
    } catch (shipError) {
      serverError.value = extractErrorMessage(shipError);
    } finally {
      saving.value = false;
    }
  }
</script>
